import { AfterViewInit, Component, Injector, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { onUpdateAnnualReportSuccessfully } from '@modeso/aumico-lib-annualreport-fe';
import { AnnualReportStep, IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import _ from 'lodash';
import { Observable, delay, find, map, of } from 'rxjs';
import { onUpdateAnnualReportFailed } from '../../../../../../aumico-lib-annualreport-fe/src/public-api';
import {
    AumicoCoreLayoutProvider,
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
} from '../../../../../../aumico-lib-core-fe/src/lib';
import { ArFeatureDataState } from '../../../_base/arFeatureDataState.enum';
import { DataState } from '../../../shared/enum/datastate.enum';
import { AnnualReportSubPage } from '../annual-report-sub-page';
import { FinancialClosingProcessAdapter } from './adapters/inbound/fcp.adapter';
import { FcpStateServiceAdapter } from './adapters/outbound/fcpState.service';
import { financialClosingProcessItemDocument } from './domain/reducers/fcp.actions';
import { FcpFeatureState } from './domain/reducers/fcp.reducer';

@UntilDestroy()
@Component({
    selector: 'aumico-financial-closing-process-page',
    templateUrl: 'financialClosingProcess.page.html',
})
export class FinancialClosingProcessPage extends AnnualReportSubPage implements AfterViewInit {
    isPartOfArNavigation$: Observable<boolean>;
    dataState: DataState = DataState.Loading;
    dataOriginState = ArFeatureDataState.UNDEFINED;

    originalAnnualReport: IAnnualReport;
    private isSaveRequestInProgress = false;

    @ViewChild('fcpAdapter')
    protected fcpAdapter: FinancialClosingProcessAdapter;

    private documentsMarkedForDeletion: string[] = [];

    constructor(
        readonly injector: Injector,
        override readonly dialog: MatDialog,
        public override readonly aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override readonly actionsListener$: ActionsSubject,
        private stateService: FcpStateServiceAdapter,
        private readonly store: Store<FcpFeatureState>,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);
        this.currentAnnualReportStep = AnnualReportStep.FINANCIAL_CLOSING_PROCESS;

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(
            AnnualReportStep.FINANCIAL_CLOSING_PROCESS,
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.providerOfAnnualReports.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        this.providerOfAnnualReports
            .getActiveAnnualReport$()
            .pipe(
                find((activeAnnual: IAnnualReport) => activeAnnual != null),
                untilDestroyed(this),
            )
            .subscribe((annualReport: IAnnualReport) => {
                this.originalAnnualReport = _.cloneDeep(annualReport);
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        this.actionsListener$
            .pipe(ofType(financialClosingProcessItemDocument.markfordeletionfcpdocuments), untilDestroyed(this))
            .subscribe((action) => {
                this.onDocumentDeleted(action.payload);
            });

        this.actionsListener$.pipe(ofType(onUpdateAnnualReportSuccessfully)).subscribe(() => {
            if (this.documentsMarkedForDeletion.length > EMPTY_ARRAY_LENGTH) {
                this.store.dispatch(
                    financialClosingProcessItemDocument.deletefcpdocuments({
                        payload: this.documentsMarkedForDeletion,
                    }),
                );
                this.documentsMarkedForDeletion = [];
            }
            this.onSaveSuccessfully();
        });

        this.stateService.dataState$.pipe(untilDestroyed(this)).subscribe((value) => {
            this.dataOriginState = value;
        });

        this.disableProceedButton = false;
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.stateService.dataState$.pipe(delay(1000), untilDestroyed(this)).subscribe((state) => {
            if (state == undefined) {
                this.dataState = DataState.Error;
            }
            if (state <= 1) {
                this.dataState = DataState.Loading;
            } else if (state == 2) {
                this.dataState = DataState.Error;
            } else {
                this.dataState = DataState.Success;
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;
                this.fcpAdapter.renderFeatureForm();
            }
        });
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            const balanceIndex = 0;
            const incomeIndex = 0;

            if (!this.originalAnnualReport.finStatements?.length) {
                return of(false);
            }

            const { template: balanceTemplate } =
                this.originalAnnualReport.finStatements[balanceIndex].templateAndAccountsMapping;
            const { template: incomeTemplate } =
                this.originalAnnualReport.finStatements[incomeIndex].templateAndAccountsMapping;

            if (!balanceTemplate || !incomeTemplate) {
                return of(false);
            }

            const originalAnnualReport = this.originalAnnualReport;
            const annualReportToSave = this.stateService.getModifiedData(this.originalAnnualReport);

            if (annualReportToSave.fcp == null) {
                return of(false);
            }

            // Case: New/Autofilled FCP
            // NOTE: This will open the pop up even if user did not do any changes
            // TODO: compare annualReportToSave.fcp with fcp prefilled data
            if (originalAnnualReport.fcp == null) {
                return of(true);
            }

            // Case: Saved FCP
            const isFcpItemsEqual = _.isEqualWith(
                originalAnnualReport.fcp.items,
                annualReportToSave.fcp.items,
                (value1, value2, key) => {
                    if (key === 'adjustedValue') {
                        return true; // Skip comparison for 'adjustedValue'
                    }
                    return undefined;
                },
            );

            const isRelevantDataUnchanged =
                annualReportToSave.fcp &&
                originalAnnualReport.fcp &&
                _.isEqual(originalAnnualReport.fcp.archivedItems, annualReportToSave.fcp.archivedItems) &&
                _.isEqual(originalAnnualReport.fcp.inputFinStatements, annualReportToSave.fcp.inputFinStatements) &&
                isFcpItemsEqual;

            if (isRelevantDataUnchanged) {
                return of(false);
            } else {
                return of(true);
            }
        }
    }

    override save(): Observable<void> {
        this.isSaveRequestInProgress = true;
        const annualReportToSave = this.stateService.getModifiedData(this.originalAnnualReport);
        this.originalAnnualReport = _.cloneDeep(annualReportToSave);
        annualReportToSave.fcp.version = annualReportToSave.finStatements?.[0]?.version;
        this.dispatchSaveAnnualReport(annualReportToSave);

        return of(void 0);
    }

    protected onSaveSuccessfully(): void {
        if (this.isSaveRequestInProgress) {
            this.isSaveRequestInProgress = false;
            this.savedSuccessfully.next(true);
            this.fcpAdapter.onUserSaveDomainModel();
            this.fireSuccessNotification();
        }
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    onDocumentDeleted(document: string): void {
        this.documentsMarkedForDeletion.push(document);
    }

    public finalizeAndContinue(): void {
        this.save();

        const currentUrl = this.router.url;
        const urlParts = currentUrl.split('/');
        urlParts[urlParts.length - 1] = this.nextStepPath;
        const newUrl = urlParts.join('/');

        this.router.navigate([newUrl]);
    }
}
